import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import TableFilterTypes from 'components/table/Filters/TableFilterTypes';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useSwitchSupportClient from 'hooks/useSwitchSupportClient';

import { USERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const UserSelectionModal = NiceModal.create(({ companyID, companyName }) => {
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const { t } = useTranslation();
	const modal = useModal();

	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedRow, setSelectedRow] = useState([]);
	const switchSupportClient = useSwitchSupportClient();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					comment: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			comment: null
		}
	});

	const { handleSubmit } = formMethods;

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const getUsers = useCallback(async () => {
		await axiosPrivate
			.post(USERS.GET_CLIENT_USER_LIST, {
				companyID,
				paging: { offset: 0, count: 10000 },
				totalRecord: false
			})
			.then((response) => {
				const { status, data } = response;
				if (status === 200) {
					setData(data.data);
					setLoading(false);
				} else {
					showError();
					AppLogger.error('Failed to get users', response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	}, []);

	useEffect(() => {
		getUsers();
	}, []);

	const columns = useMemo(
		() => [
			{
				header: 'ID',
				accessorKey: 'ID',
				size: 100,
				filterFn: TableFilterTypes.multiSelection
			},
			{
				header: 'userType',
				accessorKey: 'userType',
				showOnMobile: true,
				size: 150,
				filterFn: TableFilterTypes.multiSelection
			},
			{
				header: 'fullName',
				accessorKey: 'fullName',
				showOnMobile: true,
				size: 150,
				filterFn: TableFilterTypes.multiSelection
			},
			{
				header: 'email',
				accessorKey: 'email',
				size: 150,
				filterFn: TableFilterTypes.multiSelection
			},
			{
				header: 'mobileNumber',
				accessorKey: 'mobileNumber',
				size: 150,
				filterFn: TableFilterTypes.multiSelection
			}
		],
		[]
	);

	const onSubmit = useCallback(
		(data) => {
			setLoading(true);

			axiosPrivate
				.post(USERS.ADD_SUPPORT_ACCESS, {
					clientUserID: selectedRow[0].ID,
					supportComment: data.comment
				})
				.then((response) => {
					const { status, data } = response;

					if (status === 200) {
						switchSupportClient({
							companyID,
							clientUserId: selectedRow[0].ID,
							userType: selectedRow[0].userType,
							fullName: selectedRow[0].fullName,
							email: selectedRow[0].email,
							phone: selectedRow[0].mobileNumber,
							clientName: companyName,
							SupportAccessRequest: data.ID,
							expiryDate: dayjs().add(2, 'day').toISOString(),
							status: 'Granted'
						});
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError();
				});

			setLoading(false);
		},
		[selectedRow]
	);

	return (
		<Modal fullwidth maxWidth="large">
			<ModalTitle>
				<Typography variant="h3">{`${t('moveSupportUser')} - ${companyID} - ${companyName}`}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="users" loading={loading} required>
						<Table
							columns={columns}
							data={data}
							enableRowSelection
							selectedRows={selectedRow}
							onSelectedRowsChange={setSelectedRow}
							enableMultiRowSelection={false}
							loading={loading}
							filterable
						/>
					</FormItem>
					<FormItem label="moveSupportFormComment" loading={loading} required>
						<StringInput name="comment" />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="borderless" onClick={handleCloseModal} disabled={loading} />
				<Button
					label="move"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					loading={loading}
					disabled={selectedRow?.length === 0}
				/>
			</ModalActions>
		</Modal>
	);
});

export default UserSelectionModal;
